/**
 * P2-06b/c · The product-block frame's markup — split out of `./products-block`
 * (P17A-08) so it has zero server-only dependencies (no `getTranslator`, no
 * `server-only` import anywhere in its module graph) and can be imported
 * directly by a "use client" module.
 *
 * Two callers:
 * - `./products-block`'s `ProductsBlockLayout`/`ProductsBlockSkeleton` — the
 *   async RSC wrappers every product block (featured, new arrivals, best
 *   sellers, recommendations) actually renders, which resolve the "View all"
 *   / loading labels server-side via `getTranslator()` and pass them in here.
 * - `RecentlyViewedClient` — a genuine client island (it re-fetches when the
 *   visitor's recently-viewed list diverges from the SSR snapshot), which
 *   can't import `./products-block` at all: that module pulls in
 *   `~/i18n/request` (`import "server-only"`), which Next.js refuses to bundle
 *   into client code. It renders this view directly and supplies its own
 *   translated strings via `useTranslator()`.
 *
 * The frame itself (`HomeSection`, heading, "View all" link) has no client
 * dependency and renders as plain server HTML; `./products-grid` is the one
 * "use client" piece, holding the `<ul>` of `<ProductCard>`s — see its own
 * docblock for why the boundary sits there, which P18-19 re-measured and kept.
 */
import { ProductCardSkeleton } from "~/components/storefront/product-card/skeleton";
import type { ProductCardView } from "~/components/storefront/product-card";
import { LocaleLink } from "~/i18n/provider";
import { HomeSection } from "./home-section";
import { ProductsGrid } from "./products-grid";

export type ProductsBlockLayoutKind = "row" | "grid";

const GRID = "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4";
// Row: a horizontal snap-scroller; cards are fixed-width and swipe on touch.
// The cards are links, so keyboard focus moves through them and scrolls the
// row into view — scrollable without trapping focus. Shared with the skeleton
// below, which needs the same track geometry without the interactive grid.
const ROW =
  "-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0 " +
  // scroll-px-4: with snap-mandatory + snap-start, the first card snapped to the
  // scrollport edge and ate the row's own 16 px padding on phones (heading at
  // 16 px, card at 0). Scroll padding makes the snap positions honour it.
  "scroll-px-4 sm:scroll-px-0 " +
  "[scrollbar-width:thin] [&>li]:w-44 [&>li]:shrink-0 [&>li]:snap-start sm:[&>li]:w-56";

export function ProductsBlockLayoutView({
  title,
  viewAllHref,
  viewAllLabel,
  layout,
  products,
  rankBadges = false,
}: {
  title: string;
  viewAllHref?: string;
  /** Pre-translated "View all" label; ignored when `viewAllHref` is unset. */
  viewAllLabel?: string;
  layout: ProductsBlockLayoutKind;
  products: ProductCardView[];
  /** Overlay a "#N" rank badge on each card (best-sellers, P2-06d). */
  rankBadges?: boolean;
}) {
  return (
    <HomeSection label={title}>
      <div className="mb-6 flex items-baseline justify-between gap-4">
        <h2 className="font-heading text-2xl font-semibold sm:text-3xl">
          {title}
        </h2>
        {viewAllHref && (
          <LocaleLink
            href={viewAllHref}
            className="shrink-0 text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {viewAllLabel}
          </LocaleLink>
        )}
      </div>
      <ProductsGrid
        title={title}
        layout={layout}
        products={products}
        rankBadges={rankBadges}
      />
    </HomeSection>
  );
}

export function ProductsBlockSkeletonView({
  layout = "row",
  count = 4,
  loadingLabel,
}: {
  layout?: ProductsBlockLayoutKind;
  count?: number;
  loadingLabel: string;
}) {
  return (
    <HomeSection label={loadingLabel}>
      <div className="mb-6 h-8 w-48 animate-pulse rounded-[var(--radius-sm)] bg-surface-2" />
      <ul aria-hidden className={layout === "grid" ? GRID : ROW}>
        {Array.from({ length: count }).map((_, i) => (
          <li key={i}>
            <ProductCardSkeleton />
          </li>
        ))}
      </ul>
    </HomeSection>
  );
}
